// ***************************************
// PowerUpBarDialog
//
// This is an empty template for a new component
//
// Copyright 2015 Tingly Games B.V.
// Last Changed: 25-9-2015
// ***************************************
import { inventoryController } from "../../../game_theme/flow_fb/flowController";
import { tbEase } from "../../engine/tbEase";
import { ComponentContainer } from "../../framework/components/ComponentContainer";
import { ComponentLabel } from "../../framework/components/ComponentLabel";
import { ComponentPicture } from "../../framework/components/ComponentPicture";
import { ComponentSpine } from "../../framework/components/ComponentSpine";
import { DialogBase } from "../../framework/dialogs/DialogBase";
import { tgfDictionary } from "../../framework/tgfDictionary";
import { tgfDialogs } from "../../framework/dialogs/tgfDialogs";
import { tgfGraphics } from "../../framework/tgfGraphics";
import { tgfSettings } from "../../framework/tgfSettings";
import { PowerUpSettings, NameSettings, PowerUpSlotSettings } from "../../settings/Settings";
import { SoundManager } from "../../settings/SoundManager";
import { angrybirds_regular } from "../AssetDefinitions/FontDefinitions";
import { s_powerup_tnt, s_button_glow3, s_buttonpowerbg, s_button_rectangle_white } from "../AssetDefinitions/ImageDefinitions";
import { s_smokePuff } from "../AssetDefinitions/SpineDefinitions";
import { EventSystem } from "../Events/EventSystem";
import { Events } from "../Events/Events";
import { PowerUpSlot } from "./PowerUpSlotComponent";
import { VideoPowerUpSlot } from "./VideoPowerUpSlotComponent";
// Directives for jslint
/*global tbEase, tbVectorFont, tbGraphics, tbImage, tbSurface, tbGame */
/*global tgfComponents, tgfScale, tgfEffects, tgfDictionary, tgfAudio, tgfSettings */
tgfSettings.dialogDefaults.powerUpDialog = {
    components: [] // Components to add as children
};
// The constructor for the component
/**
 * @constructor
 * @base ComponentBase
 */
export var PowerUpBarDialog = function (properties) {
    "use strict";
    this.name = "PowerUpBarDialog"; // The name of the dialog
    // Initialize properties
    this.initialize(tgfSettings.dialogDefaults.container);
    this.setProperties(tgfSettings.dialogDefaults.powerUpDialog);
    if (properties !== undefined) {
        this.setProperties(properties);
    }
    this.initializeValues();
    this.baseContainer.x = 100;
};
// Inherit the base component
tgfDialogs.inherit(PowerUpBarDialog, DialogBase);
PowerUpBarDialog.prototype.initializeValues = function () {
    'use strict';
    /**
     * reference to the powerupslots provided by the inventory controller.
     */
    this._powerUpSlots = null;
    this._powerUpSlotComponents = [];
    this.powerUpUsed = false;
    this.disableButtons = true;
    /**
     * powerup that is selected by the user (if any). This is a string as gotten from the Settings.NameSettings object.
     */
    this._selectedPowerUp = null;
    /**
     * powerup that is selected by the user (if any). This is an index from the list of power ups inside PowerUpSettings
     */
    this._selectedPowerUpIndex = -1;
    this._selectedPowerUpIndexDisplay = -1; // Same but for only visual effects
    /**
     * The animation function that will animate the bar.
     */
    this._animationEaseFunction = this.STATE.NOTHING;
    /**
     * The time in miliseconds from one transition to the other. Used to animate the bar.
     */
    this._animationTime = 0;
    /**
     * The position of the bar dialog. Used to position the bar after animation is calculated.
     */
    this._animationPosition = 0;
    /**
     * The number of filled power ups. This is retrieved from the inventory controller.
     */
    this._filledPowerUps = 0;
    /**
     * The startposition when the hide animation is played.
     * 0 is when the bar is closed using the X button.
     * 125 is when the bar is closed after a powerup was spawned.
     */
    this._animationHideStartPosition = 0;
    this._alphaSinProgress = 0;
};
// ***************************************
// Specific methods for this component
// ***************************************
PowerUpBarDialog.prototype.getCurrentPowerUp = function () {
    return this._selectedPowerUp;
};
PowerUpBarDialog.prototype.getCurrentPowerUpIndex = function () {
    return this._selectedPowerUpIndex;
};
PowerUpBarDialog.prototype.STATE = {
    SHOW: "show",
    MOVE_DOWN: "move_down",
    MOVE_UP: "move_up",
    HIDE: "hide",
    NOTHING: "nothing",
    POWERUP_SELECTED: "pwrup_selected",
    CANCEL_POWERUP: "pwrup_cancel",
    CHANGE_POWERUP: "pwrup_change",
    NO_ANIMATION: "no_animation" //doing no animation at all, not even global UI animations (where the UI moves out of view).
};
PowerUpBarDialog.prototype.hideGlows = function () {
    'use strict';
    for (var i = 0; i < this._powerUpSlotComponents.length; i++) {
        this._powerUpSlotComponents[i].startGlow.hide();
    }
};
PowerUpBarDialog.prototype.cancelSelection = function () {
    'use strict';
    if (this._selectedPowerUpIndex >= 0) {
        this._powerUpSlotComponents[this._selectedPowerUpIndex].onCancelSelection();
    }
};
PowerUpBarDialog.prototype.onCancelPowerup = function () {
    if (!this._selectedPowerUp || this.isPlacingPowerup()) {
        return;
    }
    if (!this.powerUpUsed) {
        this.setState(this.STATE.CANCEL_POWERUP);
    }
    this._animationTime = 0;
    //this.powerUpDescription.origY = this.powerUpDescription.y - 150;
    this._selectedPowerUp = null;
    this._selectedPowerUpIndexDisplay = this._selectedPowerUpIndex;
    this._selectedPowerUpIndex = -1;
    EventSystem.dispatch(Events.POWERUP_CANCELED);
};
PowerUpBarDialog.prototype.isCancelingPowerup = function () {
    return this._animationEaseFunction === this.STATE.CANCEL_POWERUP;
};
PowerUpBarDialog.prototype.onResetingPowerups = function () {
    if (this._selectedPowerUpIndex === -1) {
        return;
    }
    for (var index = 0; index < this._components.length; index++) {
        var element = this._components[index];
        if (element.name !== "PowerUpSlotComponent") {
            continue;
        }
        if (element._slotIndex === this._selectedPowerUpIndex) {
            element.onCancelSelection();
            break;
        }
    }
    this._selectedPowerUp = null;
    this._selectedPowerUpIndexDisplay = this._selectedPowerUpIndex;
    this._selectedPowerUpIndex = -1;
};
PowerUpBarDialog.prototype.onRefillPowerups = function () {
    var powerUps = inventoryController.getPowerUpSlots(), self = this;
    for (var i = 0; i < this._powerUpSlotComponents.length; i++) {
        if (powerUps[i].powerUp === -1) {
            continue;
        }
        this._powerUpSlotComponents[i].onRefill(powerUps[i].powerUp);
        this.spines[i].setAnimation(PowerUpSettings.options[powerUps[i].powerUp].smokePuff, function (comp) {
            self.hideSpines(comp.id);
        });
        this.spines[i].bringToFront();
        this.spines[i].activate();
        this.showSpines(i);
    }
};
PowerUpBarDialog.prototype.showSpines = function (num) {
    if (typeof (num) !== 'number') {
        for (var i = 0; i < this.spines.length; i++) {
            if (!this.spines[i].origPos) {
                this.spines[i].origPos = {
                    x: this.spines[i].spineClone.animation["transform"]["position"]["x"],
                    y: this.spines[i].spineClone.animation["transform"]["position"]["y"]
                };
            }
            var origX = this.spines[i].origPos.x;
            var origY = this.spines[i].origPos.y;
            if (origX === this.spines[i].spineClone.animation["transform"]["position"]["x"] && origX === this.spines[i].spineClone.animation["transform"]["position"]["y"]) {
                continue;
            }
            this.spines[i].spineClone.animation["transform"]["position"]["x"] = origX;
            this.spines[i].spineClone.animation["transform"]["position"]["y"] = origY;
            this.spines[i].setAlpha(1);
        }
    }
    else {
        if (!this.spines[num].origPos) {
            this.spines[num].origPos = {
                x: this.spines[num].spineClone.animation["transform"]["position"]["x"],
                y: this.spines[num].spineClone.animation["transform"]["position"]["y"]
            };
        }
        var origX = this.spines[num].origPos.x;
        var origY = this.spines[num].origPos.y;
        if (origX === this.spines[num].spineClone.animation["transform"]["position"]["x"] && origX === this.spines[num].spineClone.animation["transform"]["position"]["y"]) {
            return;
        }
        this.spines[num].spineClone.animation["transform"]["position"]["x"] = origX;
        this.spines[num].spineClone.animation["transform"]["position"]["y"] = origY;
        this.spines[num].setAlpha(1);
    }
};
PowerUpBarDialog.prototype.hideSpines = function (num) {
    if (typeof (num) !== 'number') {
        for (var i = 0; i < this.spines.length; i++) {
            this.spines[i].spineClone.animation["transform"]["position"]["x"] = -9999;
            this.spines[i].spineClone.animation["transform"]["position"]["y"] = -9999;
        }
    }
    else {
        this.spines[num].spineClone.animation["transform"]["position"]["x"] = -9999;
        this.spines[num].spineClone.animation["transform"]["position"]["y"] = -9999;
    }
};
PowerUpBarDialog.prototype.onUsePowerUpClicked = function (powerUpType, powerUpIndex) {
    EventSystem.dispatch(Events.POWERUP_CLICKED);
    if (this.isPlacingPowerup()) {
        return;
    }
    var changingPowerup = false;
    if (this._animationEaseFunction === this.STATE.POWERUP_SELECTED || this._animationEaseFunction === this.STATE.CHANGE_POWERUP) {
        if (powerUpIndex !== this._selectedPowerUpIndex) {
            //powerUpSlots[this._selectedPowerUpIndex].onUsePowerUpClicked();
            for (var index = 0; index < this._components.length; index++) {
                var element = this._components[index];
                if (element.name !== "PowerUpSlotComponent") {
                    continue;
                }
                if (element._slotIndex === this._selectedPowerUpIndex) {
                    element.onCancelSelection();
                    changingPowerup = true;
                    break;
                }
            }
        }
        else {
            return false;
        }
    }
    if (changingPowerup) {
        this.setState(this.STATE.CHANGE_POWERUP);
    }
    else {
        this.setState(this.STATE.POWERUP_SELECTED);
    }
    this._animationTime = 0;
    if (!changingPowerup) {
        this.powerUpDescription.origY = this.powerUpDescription.y;
    }
    for (var index = 0; index < PowerUpSettings.options.length; index++) {
        var element = PowerUpSettings.options[index];
        if (element.kind === powerUpType) {
            if (element.kind === NameSettings.swapPigs && this.canSwapPigs() === false) {
                this.powerUpDescription.setText(tgfDictionary.getText(PowerUpSettings.cannotSwapPigsMessage));
            }
            else {
                this.powerUpDescription.setText(tgfDictionary.getText(element.desc));
            }
            break;
        }
    }
    //this._powerUpSlotComponents[powerUpIndex].setScale(1,1);
    SoundManager.playEffect("swooshOut", SoundManager.PowerUpCollection.Bar, 150);
    SoundManager.playEffect("select", SoundManager.PowerUpCollection[powerUpType]);
    this._selectedPowerUp = powerUpType;
    this._selectedPowerUpIndexDisplay = powerUpIndex;
    this._selectedPowerUpIndex = powerUpIndex;
    return true;
};
PowerUpBarDialog.prototype.setPowerUpSlot = function (powerUpSlotComponent, index, powerUpIndex) {
    'use strict';
    powerUpSlotComponent.setIndex(index);
    powerUpSlotComponent.setPowerUpIndex(powerUpIndex);
};
PowerUpBarDialog.prototype.getPowerUpName = function (powerUpIndex) {
    return PowerUpSettings.options[powerUpIndex].kind;
};
PowerUpBarDialog.prototype.getPowerUpImage = function (powerUpIndex) {
    return PowerUpSettings.options[powerUpIndex].image;
};
// ***************************************
// Preparing, showing and activating
//      - These can be removed if no processing is required
// ***************************************
PowerUpBarDialog.prototype.createPowerUpSlots = function () {
    'use strict';
    var powerUpSlots = inventoryController.getPowerUpSlots();
    this._filledPowerUps = inventoryController.getFilledPowerUps();
    this.spines = [];
    for (var i = 0; i < powerUpSlots.length; ++i) {
        var powerUpSlot = powerUpSlots[i];
        var powerUpSlotComponent = new PowerUpSlot({
            x: i * PowerUpSlotSettings.distanceBetweenPowerUpSlots + PowerUpSlotSettings.leftMostPosition,
            y: 0,
            isCancelingPowerup: this.isCancelingPowerup.bind(this),
            onUsePowerUpClicked: this.onUsePowerUpClicked.bind(this),
            onCancelPowerup: this.onCancelPowerup.bind(this)
        });
        this.setPowerUpSlot(powerUpSlotComponent, i, powerUpSlot.powerUp);
        powerUpSlotComponent.startGlow = this._glows[i];
        if (powerUpSlotComponent._powerUpIndex < 0) {
            this._glows[i].setAlpha(0);
        }
        this._powerUpSlotComponents.push(powerUpSlotComponent);
        this.spines[i] = new ComponentSpine({
            id: i,
            alpha: 0,
            x: 0,
            y: 0,
            spineAnimation: s_smokePuff,
            repeat: false
        });
        powerUpSlotComponent.addComponent(this.spines[i]);
        this.addComponent(powerUpSlotComponent);
    }
    var refillButton = new VideoPowerUpSlot({
        image: s_powerup_tnt,
        x: powerUpSlots.length * PowerUpSlotSettings.distanceBetweenPowerUpSlots + PowerUpSlotSettings.leftMostPosition,
        y: 0,
        isCancelingPowerup: this.isCancelingPowerup.bind(this),
        onUsePowerUpClicked: this.onUsePowerUpClicked.bind(this),
        onCancelPowerup: this.onCancelPowerup.bind(this),
        onResetingPowerups: this.onResetingPowerups.bind(this),
        onRefillPowerups: this.onRefillPowerups.bind(this)
    });
    // this.setPowerUpSlot(refillButton, powerUpSlots.length, "refill");
    // this.addComponent(refillButton);
};
// Processing required when preparing the component for use
PowerUpBarDialog.prototype.doPrepare = function () {
    "use strict";
    this.powerUpFinished = false;
    var powerUpBar = this;
    this.powerUpDescription = new ComponentLabel({
        text: '',
        x: 0,
        y: -800,
        font: {
            fontFace: angrybirds_regular.fontFace,
            fontSize: 60,
            fillColor: "#fefefe",
            align: "center",
            baseline: "middle",
            wordWrap: true,
            wordWrapWidth: tgfGraphics.areaWidth,
            shadow: { active: true, color: 'rgba(50, 49, 65, 0.7)', offsetX: 5, offsetY: 5, blur: 1, distance: 1 }
        },
    });
    this.addComponent(this.powerUpDescription);
    if (!this._glows) {
        this._glows = [];
    }
    this.buttonAddPwrContainer = new ComponentContainer({
        x: 353,
        y: 0
    });
    this.addComponent(this.buttonAddPwrContainer);
    for (var i = 0; i < PowerUpSettings.maxSlotAmount; i++) {
        var startGlow = new ComponentPicture({
            image: s_button_glow3,
            x: PowerUpSlotSettings.leftMostPosition + (PowerUpSlotSettings.distanceBetweenPowerUpSlots * i),
            y: 0,
            xScale: 2,
            yScale: 2,
            alpha: 0.7
        });
        this.buttonAddPwrContainer.addComponent(startGlow);
        this._glows.push(startGlow);
    }
    this.buttonAddPowerUpBg = new ComponentPicture({
        image: s_buttonpowerbg,
        xScale: 1.07,
        yScale: 1.07,
        x: 0,
        y: 0,
        alpha: 1
    });
    this.powerUpBgBar = new ComponentPicture({
        image: s_button_rectangle_white,
        xScale: 1.8,
        yScale: 1.2,
        x: 0,
        y: 5
    });
    this.buttonAddPwrContainer.addComponent(this.powerUpBgBar);
    this.createPowerUpSlots();
    // Add the child components
    if (this.components) {
        for (i = 0; i < this.components.length; i += 1) {
            this.addComponent(this.components[i]);
        }
    }
    this._slotElements = [];
    for (var index = 0; index < this._components.length; index++) {
        var element = this._components[index];
        if (element.name === "PowerUpSlotComponent") {
            this._slotElements.push(element);
        }
        else if (element.name === "VideoPowerUpSlotComponent") {
            this._slotElements.push(element);
        }
    }
    this._enableButtons(false);
};
PowerUpBarDialog.prototype.handleEndStep = function (timeStep) {
    'use strict';
    PowerUpBarDialog.uber.handleEndStep.call(this, timeStep);
    this.handleAnimation(timeStep);
};
PowerUpBarDialog.prototype.setAnimationPositionY = function (y) {
    'use strict';
    if (this._animationEaseFunction === this.STATE.NOTHING) {
        this._animationPosition = y;
    }
};
PowerUpBarDialog.prototype._enableButtons = function (enableButtons) {
    'use strict';
    if (enableButtons) {
        for (var i = 0; i < this._slotElements.length; i++) {
            this._slotElements[i]._enableInput();
        }
        for (var i = 0; i < this._powerUpSlotComponents.length; i++) {
            for (var y = 0; y < this._powerUpSlotComponents[i]._powerUpImages.length; y++) {
                this._powerUpSlotComponents[i]._powerUpImages[y]._disabled = false;
            }
        }
    }
    else {
        for (var i = 0; i < this._slotElements.length; i++) {
            this._slotElements[i]._disableInput();
        }
        for (var i = 0; i < this._powerUpSlotComponents.length; i++) {
            for (var y = 0; y < this._powerUpSlotComponents[i]._powerUpImages.length; y++) {
                this._powerUpSlotComponents[i]._powerUpImages[y]._disabled = true;
            }
        }
    }
};
PowerUpBarDialog.prototype.handleAnimation = function (timeStep) {
    'use strict';
    var duration = 0.35;
    this._animationTime += timeStep * PowerUpSettings.powerUpBarAnims.speed;
    switch (this._animationEaseFunction) {
        case this.STATE.POWERUP_SELECTED:
            this.powerUpDescription.y = tbEase.easeInQuadratic(this._animationTime * 0.001, this.powerUpDescription.origY, PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance, 0.35);
            this._animationPosition = tbEase.easeInQuadratic(this._animationTime * 0.001, 0, 75, duration);
            if (this.powerUpDescription.y >= (this.powerUpDescription.origY + PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance)) {
                this.powerUpDescription.y = this.powerUpDescription.origY + PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance;
            }
            if (this._animationPosition >= 40) {
                this._animationPosition = 40;
            }
            break;
        case this.STATE.MOVE_DOWN:
            this._animationPosition = tbEase.easeInQuintic(this._animationTime * 0.001, 0, 125, duration);
            if (this._animationPosition >= 345) {
                this._animationPosition = 345;
                this._animationEaseFunction = this.STATE.NO_ANIMATION;
            }
            break;
        case this.STATE.MOVE_UP:
            this._animationPosition = tbEase.easeInQuintic(this._animationTime * 0.001, 125, -125, duration);
            if (this._animationPosition <= 0) {
                this._animationEaseFunction = this.STATE.NOTHING;
                this._animationPosition = 0;
            }
            break;
        case this.STATE.HIDE:
            var descGone = false;
            if (this.powerUpDescription.y > this.powerUpDescription.origY) {
                this.powerUpDescription.y = tbEase.easeInQuintic(this._animationTime * 0.001, this.powerUpDescription.origY + PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance, -PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance, 0.35);
            }
            else {
                this.powerUpDescription.y = this.powerUpDescription.origY;
                descGone = true;
            }
            this._animationPosition = tbEase.easeInQuintic(this._animationTime * 0.001, 0, 125, duration);
            if (this._animationPosition >= 345) {
                this._animationPosition = 345;
                if (descGone) {
                    this._animationEaseFunction = this.STATE.NO_ANIMATION;
                    this.powerUpFinished = true;
                }
            }
            break;
        case this.STATE.CHANGE_POWERUP:
            this.powerUpDescription.y = tbEase.easeInQuadratic(this._animationTime * 0.001, this.powerUpDescription.origY, PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance, 0.35);
            if (this.powerUpDescription.y >= (this.powerUpDescription.origY + PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance)) {
                this.powerUpDescription.y = this.powerUpDescription.origY + PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance;
            }
            this._animationPosition = tbEase.easeInQuadratic(this._animationTime * 0.001, 0, 75, duration);
            if (this._animationPosition >= 40) {
                this._animationPosition = 40;
            }
            this._slotElements[this._selectedPowerUpIndexDisplay].setPosition(0, (this._animationPosition * -0.8) * PowerUpSlotSettings.animationSpeedScalar);
            for (var i = 0; i < this._slotElements.length; i++) {
                if (i != this._selectedPowerUpIndexDisplay) {
                    this._slotElements[i].setPosition(0, 32 * PowerUpSlotSettings.animationSpeedScalar);
                }
            }
            break;
        case this.STATE.SHOW:
            this._animationPosition = tbEase.easeInQuintic(this._animationTime * 0.001, PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance, -PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance, duration);
            if (this._animationPosition <= 0) {
                this._animationEaseFunction = this.STATE.NOTHING;
                this._animationPosition = 0;
            }
            break;
        case this.STATE.CANCEL_POWERUP:
            this.powerUpDescription.y = tbEase.easeInQuadratic(this._animationTime * 0.001, this.powerUpDescription.origY + PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance, -PowerUpSettings.powerUpBarAnims.powerupDescriptionDistance, 0.35);
            this._animationPosition = tbEase.easeInQuadratic(this._animationTime * 0.001, 40, -40, duration);
            if (this.powerUpDescription.y < this.powerUpDescription.origY) {
                this.powerUpDescription.y = this.powerUpDescription.origY;
            }
            if (this._animationPosition < 0) {
                this._animationPosition = 0;
                this.setState(this.STATE.NOTHING);
                this.blockHiding = true;
                this._selectedPowerUp = null;
                this._selectedPowerUpIndex = -1;
                this._selectedPowerUpIndexDisplay = -1;
                return;
            }
            break;
        default:
            this._animationTime = 0;
            break;
    }
    for (var i = 0; i < this._slotElements.length; i++) {
        if (this._slotElements[i]) {
            this._slotElements[i].animate(timeStep, (i === this._selectedPowerUpIndex));
        }
        if (this._animationEaseFunction === this.STATE.CHANGE_POWERUP) {
            continue;
        }
        if (i != this._selectedPowerUpIndexDisplay || this.powerUpUsed === true) {
            var ignoreIndex = this._animationEaseFunction === this.STATE.POWERUP_SELECTED || this._animationEaseFunction === this.STATE.CANCEL_POWERUP || this._animationEaseFunction === this.STATE.CHANGE_POWERUP;
            this._slotElements[i].setPosition(0, (this._animationPosition * (0.2 * ((ignoreIndex ? 1 : i) + 3))) * PowerUpSlotSettings.animationSpeedScalar);
        }
        else {
            this._slotElements[i].setPosition(0, (this._animationPosition * -0.8) * PowerUpSlotSettings.animationSpeedScalar);
        }
    }
    if (this._animationEaseFunction !== this.STATE.CHANGE_POWERUP) {
        this.buttonAddPwrContainer.setPosition(0, (this._animationPosition * 0.95) * PowerUpSlotSettings.animationSpeedScalar);
    }
};
PowerUpBarDialog.prototype.onPowerUpPlaced = function () {
    this._animationHideStartPosition = 125;
    this._animationTime = 0;
    this.setState(this.STATE.HIDE);
    this.powerUpUsed = true;
};
PowerUpBarDialog.prototype.onPowerUpSpawned = function (powerUpType, hideUI) {
    'use strict';
    if (hideUI === true) {
        this._animationHideStartPosition = 125;
        this.setState(this.STATE.HIDE);
    }
    this._selectedPowerUp = null;
};
PowerUpBarDialog.prototype.setState = function (state) {
    "use strict";
    if (state) {
        console.log("%cSetting PowerUpState to %s", "background: red; color: yellow;", state);
        this._animationEaseFunction = state;
        if (state === this.STATE.SHOW) {
            this.powerUpFinished = false;
            SoundManager.playEffect("swooshIn", SoundManager.PowerUpCollection.Bar, 150);
            this._animationHideStartPosition = 0;
        }
        else if (state === this.STATE.HIDE) {
            this._animationTime = 0;
            this.powerUpFinished = true;
        }
    }
};
